// ============================================================================
// Sentry init for the frontend - gated on REACT_APP_SENTRY_DSN
//
// beforeSend hook scrubs PII from outgoing events:
//   - drops sensitive request headers (authorization, cookie, x-api-key)
//   - recursively redacts known-sensitive object keys in extra/contexts
//   - depth capped at 5 to avoid runaway recursion on cyclic objects
// ============================================================================

const SENSITIVE_KEY_PATTERN =
  /password|token|secret|apikey|api_key|jwt|authorization|pan|aadhaar|bank|gst|otp/i;

const SENSITIVE_HEADERS = new Set([
  'authorization',
  'cookie',
  'x-api-key',
  'x-auth-token',
]);

export function scrubSensitiveData(obj, depth = 0) {
  if (depth > 5 || obj == null) return obj;
  if (typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) {
    return obj.map((v) => scrubSensitiveData(v, depth + 1));
  }
  const out = {};
  for (const key of Object.keys(obj)) {
    if (SENSITIVE_KEY_PATTERN.test(key)) {
      out[key] = '[REDACTED]';
    } else {
      out[key] = scrubSensitiveData(obj[key], depth + 1);
    }
  }
  return out;
}

function scrubHeaders(headers) {
  if (!headers || typeof headers !== 'object') return headers;
  const out = {};
  for (const key of Object.keys(headers)) {
    if (SENSITIVE_HEADERS.has(key.toLowerCase())) {
      out[key] = '[REDACTED]';
    } else {
      out[key] = headers[key];
    }
  }
  return out;
}

function beforeSendHook(event) {
  try {
    if (event?.request?.headers) {
      event.request.headers = scrubHeaders(event.request.headers);
    }
    if (event?.extra) {
      event.extra = scrubSensitiveData(event.extra, 0);
    }
    if (event?.contexts) {
      event.contexts = scrubSensitiveData(event.contexts, 0);
    }
  } catch {
    // Never let the scrubber crash event delivery
  }
  return event;
}

export function initSentry() {
  const dsn = process.env.REACT_APP_SENTRY_DSN;
  if (!dsn) return;
  try {
    // eslint-disable-next-line global-require
    const Sentry = require('@sentry/react');
    Sentry.init({
      dsn,
      environment: process.env.NODE_ENV || 'production',
      tracesSampleRate: 0.1,
      release: process.env.REACT_APP_SENTRY_RELEASE,
      ignoreErrors: [
        'ResizeObserver loop limit exceeded',
        'Non-Error promise rejection captured',
      ],
      beforeSend: beforeSendHook,
    });
    console.log('[sentry] frontend monitoring enabled');
  } catch (e) {
    console.warn('[sentry] init failed:', e.message);
  }
}
